import * as React from "react";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Loading is a SKELETON, never a placeholder number.
 *
 * Nothing in this file renders a digit, a percentage or a state pill. A
 * skeleton says "not loaded yet"; a grey zero says "we measured zero", and the
 * two must never be confused.
 */
export type LoadingVariant = "page" | "table" | "card" | "list";

export interface LoadingSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: LoadingVariant;
  /** Rows for "table" and "list"; cards for "card". */
  rows?: number;
  /** Columns for "table". */
  columns?: number;
  /** Announced to assistive technology, e.g. "Loading posture findings". */
  label?: string;
}

export function LoadingSkeleton({
  variant = "page",
  rows = 6,
  columns = 5,
  label = "Loading",
  className,
  ...props
}: LoadingSkeletonProps): React.ReactElement {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="loading-skeleton"
      data-variant={variant}
      className={cn("w-full", className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
      {variant === "page" ? <PageSkeleton /> : null}
      {variant === "table" ? <TableSkeleton rows={rows} columns={columns} /> : null}
      {variant === "card" ? <CardSkeleton count={rows} /> : null}
      {variant === "list" ? <ListSkeleton rows={rows} /> : null}
    </div>
  );
}

function PageSkeleton(): React.ReactElement {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-3 rounded-lg border p-5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-2 w-full" />
          </div>
        ))}
      </div>
      <TableSkeleton rows={5} columns={5} />
    </div>
  );
}

function TableSkeleton({ rows, columns }: { rows: number; columns: number }): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex gap-4 border-b bg-muted/40 px-3 py-2.5">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-4 border-b px-3 py-3 last:border-0">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

function CardSkeleton({ count }: { count: number }): React.ReactElement {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col gap-3 rounded-lg border p-5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-24" />
          <Skeleton className="h-2 w-full" />
        </div>
      ))}
    </div>
  );
}

function ListSkeleton({ rows }: { rows: number }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-md border p-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
